import AgencyHelper from "features/Agency/agencyHelper";
import ReconciliationReportHelper from "features/ReconciliationReport/ReconciliationReportHelper";
import KTBSButton, { KTBSButtonColor, KTBSButtonSize } from "general/components/OtherKeenComponents/KTBSButton";
import KTLabel, { KTLabelSize } from "general/components/OtherKeenComponents/KTLabel";
import CustomCard from "general/components/Others/CustomCard";
import VITableRowDropDown from "general/components/VITableRowDropDown";
import AppColors from "general/constants/AppColors";
import Utils from "general/utils/Utils";
import useRouter from "hooks/useRouter";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { AppCustomDialogHelper } from "general/components/AppDialogCustom";
import BreadcrumbHelper from "general/components/CustomBreadcrumb/BreadcrumbHelper";
import ToastHelper from "general/helpers/ToastHelper";
import UIHelper from "general/helpers/UIHelper";
import _ from "lodash";
import AppConstants from "general/constants/AppConstants";
import DialogEditReconciliationView from "features/ReconciliationReport/SaleReconciliationReport/components/DialogEditReconciliationView";
import AgencyReconciliationCardView from "features/ReconciliationReport/SaleReconciliationReport/components/AgencyReconciliationCardView";
import AppTabs from "general/components/AppTabs";
import { AnimatePresence, motion } from 'framer-motion';
import GroupRevenueView from "features/ReconciliationReport/InvestorReconciliationReport/components/GroupRevenueView";
import StationEnergyView from "features/ReconciliationReport/InvestorReconciliationReport/components/StationEnergyView";
import EnergyBillView from "features/ReconciliationReport/InvestorReconciliationReport/components/EnergyBillView";
import AppConfigs from "general/constants/AppConfigs";
import LandlordRevenueView from "../LandlordRevenueView";

LandlordReconciliationDetailView.propTypes = {};

function LandlordReconciliationDetailView(props) {
    // MARK: ---- PROPERTIES ----
    const router = useRouter();
    const { t } = useTranslation();
    const { reconciliationId } = router.query;
    const agencyId = router?.query?.agencyId;
    const [reconciliationDetail, setReconciliationDetail] = useState(null);
    const [agencyDetail, setAgencyDetail] = useState(null);
    const [groupInvestmentList, setGroupInvestmentList] = useState(null);
    const { user } = useSelector((state) => state.auth);
    const adminRole =
        user?.role === AppConstants.userRole.admin || user?.role === AppConstants.userRole.operator;
    const isOrganizationAgency = useMemo(() => {
        return reconciliationDetail?.agency?.type === 'ORGANIZATION';
    }, [reconciliationDetail]);

    useEffect(() => {
        handleGetLandlordReconciliationDetail();
    }, [reconciliationId, agencyId]);

    const [selectedTab, setSelectedTab] = useState('Doanh thu theo mặt bằng');
    const tabs = useMemo(() => {
        return [
            'Doanh thu theo mặt bằng',
            'Tiền điện trạm sạc',
            'Hóa đơn tiền điện',
        ].reverse();
    }, []);

    const hash = router?.location?.hash;

    useEffect(() => {
        if (hash === '#group-revenue') setSelectedTab('Doanh thu theo mặt bằng');
        if (hash === '#station-energy') setSelectedTab('Tiền điện trạm sạc');
        if (hash === '#energy-bill') setSelectedTab('Hóa đơn tiền điện');
    }, [hash]);

    const landlordRevenueView = useMemo(() => {
        return (
            <LandlordRevenueView
                agencyId={agencyId}
                landlordRevenueList={reconciliationDetail?.metadata?.energyCommissionByPremise}
                landlordRevenueTotal={reconciliationDetail?.metadata?.energyCommissionByPremise?.length}
                filter={{
                    page: 0,
                    limit: AppConfigs.defaultPageLimit,
                }}
                reconciliationDetail={reconciliationDetail}
            />
        );
    }, [agencyDetail, reconciliationDetail]);

    const stationEnergyView = useMemo(() => {
        const formatData = (reconciliationDetail?.metadata?.premises || [])
            .flatMap(p => p.chargeStations || [])
            .filter(cs => cs?.isAgencyPayEnergyCost === 1);

        return (
            <StationEnergyView
                agencyId={agencyId}
                primaryRole='LANDLORD'
                stationEnergyList={formatData}
                stationEnergyTotal={formatData?.length}
                filter={{
                    page: 0,
                    limit: AppConfigs.defaultPageLimit,
                }}
                reconciliationDetail={reconciliationDetail}
            />
        );
    }, [agencyId, reconciliationDetail]);

    const energyBillView = useMemo(() => {
        const formatData = (reconciliationDetail?.metadata?.premises || [])
            .flatMap(p => p.chargeStations || [])
            .filter(cs => cs?.isAgencyPayEnergyCost === 1);

        return (
            <EnergyBillView
                agencyId={agencyId}
                energyBillList={formatData}
                energyBillTotal={formatData?.length}
                filter={{
                    page: 0,
                    limit: AppConfigs.defaultPageLimit,
                }}
                reconciliationDetail={reconciliationDetail}
            />
        );
    }, [agencyDetail, reconciliationDetail]);

    // MARK: ---- METHOD ----
    const handleGetLandlordReconciliationDetail = () => {
        if (reconciliationId) {
            AgencyHelper.API.requestGetReconciliationDetail(reconciliationId).then(
                (reconciliationDetail) => {
                    setReconciliationDetail(reconciliationDetail);
                    setAgencyDetail(reconciliationDetail?.agency);
                    setGroupInvestmentList(reconciliationDetail?.metadata?.groupShare?.investments);
                }
            );
        } else if (agencyId) {
            const paramGetReconciliationList = {
                agencyId,
            };
            AgencyHelper.API.requestGetReconciliationList(paramGetReconciliationList).then(
                (reconciliationList) => {
                    setReconciliationDetail(_.maxBy(reconciliationList, 'reconciliationId'));
                }
            );
        }
    }

    const handleSelectedTab = (newTab) => {
        if (selectedTab === newTab) return;

        if (newTab === 'Doanh thu theo mặt bằng') {
            router.navigate('#group-revenue');
        }
        if (newTab === 'Tiền điện trạm sạc') {
            router.navigate('#station-energy');
        }
        if (newTab === 'Hóa đơn tiền điện') {
            router.navigate('#energy-bill');
        }

        setSelectedTab(newTab);
    };

    const SaleReconciliationInfo = () => {
        return (
            <div
                className="row m-0 position-relative p-3 bg-white rounded-lg"
                style={{
                    border: `1px solid ${AppColors.gray[300]}`,
                    boxShadow: '0px 7px 8px -3px #0000000D',
                }}
            >
                <div className="d-flex align-items-center w-100 p-0">
                    <div className="d-flex flex-column w-100">
                        <div className="d-flex align-items-center gap-2" style={{ overflow: 'hidden' }}>
                            <div
                                className="font-weight-bolder"
                                style={{
                                    color: AppColors.gray[800],
                                    fontSize: Utils.returnFontSize(18),
                                    fontWeight: '600',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                }}
                            >
                                {agencyDetail?.name}{" "}
                                • {agencyDetail?.agencyCode ?? agencyDetail?.agencyId}
                            </div>

                            <span className="min-w-100px ml-3">
                                <KTLabel
                                    text={ReconciliationReportHelper.getReconciliationStatusName(reconciliationDetail?.status)}
                                    isInline={true}
                                    size={KTLabelSize.medium}
                                    additionalClassName={`label-light-${ReconciliationReportHelper.getReconciliationStatusColor(
                                        reconciliationDetail?.status
                                    )}`}
                                />
                            </span>
                        </div>
                        <div style={{ fontSize: Utils.returnFontSize(14), color: AppColors.gray[700] }}>
                            Đối soát doanh thu chủ mặt bằng tháng {Utils.formatDateTimeToString(reconciliationDetail?.month, 'MM/yyyy')}
                        </div>
                    </div>

                    <div className="d-flex gap-3">
                        {!adminRole && reconciliationDetail?.status === 'ADMIN_SIGNED' &&
                            <KTBSButton
                                color={KTBSButtonColor.success}
                                title={t('Ký đối soát')}
                                size={KTBSButtonSize.default}
                                borderRadius={5}
                                buttonTitleFontWeight="600"
                                onPress={() => ReconciliationReportHelper.handleAgencySignReconciliation(reconciliationDetail, handleGetLandlordReconciliationDetail)}
                                height={43}
                                buttonTitleFontSize={14}
                            />
                        }
                        {adminRole && reconciliationDetail?.status === 'CREATED' &&
                            <KTBSButton
                                color={KTBSButtonColor.success}
                                title={t('Duyệt & ký đối soát')}
                                size={KTBSButtonSize.default}
                                borderRadius={5}
                                buttonTitleFontWeight="600"
                                onPress={() => ReconciliationReportHelper.handleAdminSignReconciliation(reconciliationDetail, handleGetLandlordReconciliationDetail)}
                                height={43}
                                buttonTitleFontSize={14}
                            />
                        }
                        <VITableRowDropDown
                            toggleElement={
                                <a
                                    href="#"
                                    className="btn btn-sm btn-clean btn-icon h-45px w-45px hover-opacity-60"
                                    style={{ backgroundColor: AppColors.gray[200] }}
                                    onClick={(e) => {
                                        e.preventDefault();
                                    }}
                                >
                                    <i
                                        className="fa-solid fa-ellipsis"
                                        style={{ color: AppColors.gray[700], fontSize: '1.1rem' }}
                                    />
                                </a>
                            }
                            menuItem={[
                                // {
                                //     iconElement: <i className="fa-regular fa-pen-to-square"></i>,
                                //     text: t('edit'),
                                //     click: () => {
                                //         AppCustomDialogHelper.show(
                                //             `Sửa đối soát tháng ${Utils.formatDateTimeToString(reconciliationDetail?.month, 'MM/YYYY')}`,
                                //             <DialogEditReconciliationView
                                //                 item={reconciliationDetail}
                                //                 onCancel={() => {
                                //                     AppCustomDialogHelper.hide();
                                //                 }}
                                //                 onDone={() => {
                                //                     ToastHelper.showSuccess('Cập nhật đối soát thành công!')
                                //                     AppCustomDialogHelper.hide();
                                //                     handleGetLandlordReconciliationDetail();
                                //                 }}
                                //             />,
                                //             'md',
                                //             true,
                                //             true
                                //         )
                                //     },
                                //     show: adminRole && (reconciliationDetail?.reconciliationType !== 'PAPER' ||
                                //         reconciliationDetail?.reconciliationType === 'PAPER' && reconciliationDetail?.status !== 'COMPLETED'
                                //     ),
                                // },
                                // {
                                //     iconElement: <i className="fa-regular fa-memo-circle-info"></i>,
                                //     text: 'Đơn hàng liên quan',
                                //     click: () => {
                                //         if (!adminRole) {
                                //             router.navigate(`/order?month=${reconciliationDetail?.month}`);
                                //         } else {
                                //             router.navigate(`/order?agencyId=${reconciliationDetail?.agency?.agencyId}&month=${reconciliationDetail?.month}`);
                                //         }
                                //     },
                                //     show: true,
                                // },
                                {
                                    iconElement: <i className="fa-regular fa-file-chart-column"></i>,
                                    text: t('Xem file đối soát'),
                                    click: () => {
                                        if (Utils.isValid(reconciliationDetail?.document)) {
                                            Utils.openInNewTab(Utils.getFullUrl(reconciliationDetail?.document));
                                        }
                                    },
                                    show: !!reconciliationDetail?.document,

                                },
                                {
                                    iconElement: <i className="far fa-download"></i>,
                                    text: t('Tải đối soát excel'),
                                    click: () => Utils.openInCurrentTab(reconciliationDetail?.documentXLSX),
                                    show: !!reconciliationDetail?.documentXLSX,

                                },
                                {
                                    iconElement: <i className="far fa-message-exclamation"></i>,
                                    text: 'Gửi phản hồi',
                                    click: () => ReconciliationReportHelper.showDialogReconciliationFeedback(reconciliationDetail, adminRole, handleGetLandlordReconciliationDetail),
                                    show: !adminRole && reconciliationDetail?.status !== 'AGENCY_SIGNED',
                                },
                                {
                                    iconElement: <i className="far fa-message-exclamation"></i>,
                                    text: 'Xem phản hồi',
                                    click: () => ReconciliationReportHelper.showDialogReconciliationFeedback(reconciliationDetail, adminRole, handleGetLandlordReconciliationDetail),
                                    show: adminRole && !!reconciliationDetail?.feedback,
                                },
                            ]}
                            singleBtnBgColor={AppColors.gray[200]}
                        />
                    </div>
                </div>
            </div>
        );
    };

    const groupCommissionList = useMemo(() => {
        const revenueBeforeTax = Math.round(reconciliationDetail?.amount);
        const personalIncomeTax = Math.round(revenueBeforeTax * reconciliationDetail?.taxRate);
        const revenueAfterTax = !isOrganizationAgency ? Math.round(revenueBeforeTax - personalIncomeTax) : revenueBeforeTax;

        return [
            {
                name: 'Tổng doanh thu/phí thuê theo mặt bằng',
                value: Number(revenueBeforeTax),
                unit: 'VNĐ',
                color: '#30BD6F',
                icon: 'fa-solid fa-badge-dollar',
                show: adminRole,
            },
            {
                name: `Thuế TNCN (${reconciliationDetail?.taxRate * 100}%)`,
                value: Number(personalIncomeTax),
                unit: 'VNĐ',
                color: '#FE6358',
                icon: 'fa-solid fa-badge-percent',
                show: !isOrganizationAgency,
            },
            {
                name: 'Doanh thu chủ mặt bằng được nhận',
                value: Number(reconciliationDetail?.netAmount),
                unit: 'VNĐ',
                color: '#FFAA24',
                icon: 'fa-solid fa-badge-dollar',
                show: true,
            }
        ];
    }, [reconciliationDetail, adminRole, isOrganizationAgency]);

    const GroupCommissionReconciliationInfo = () => {
        return (
            <div
                className="overflow-hidden position-relative mt-4"
                style={{
                    border: `1px solid ${AppColors.gray[300]}`,
                    borderRadius: '10px',
                }}
            >
                <div
                    style={{
                        backgroundColor: '#fff',
                        borderBottomLeftRadius: '10px',
                        borderBottomRightRadius: '10px',
                    }}
                >
                    {/* barchart */}
                    <div className="w-100 d-flex flex-column gap-1 p-4"
                        style={{
                            borderBottom: `1px solid ${AppColors.gray[300]}`
                        }}
                    >
                        <div style={UIHelper.buildFontStyle('700', '14px', AppColors.gray[800])}>
                            Doanh thu chủ mặt bằng được hưởng
                        </div>
                    </div>
                    {/* body */}
                    <div className="w-100 d-flex p-4 gap-3">
                        {groupCommissionList?.filter(cardInfo => cardInfo?.show)
                            ?.map((cardInfo, index) => (
                                <div
                                    key={index}
                                    className="flex-grow-1 p-0"
                                >
                                    <AgencyReconciliationCardView
                                        name={cardInfo.name}
                                        value={cardInfo.value}
                                        unit={cardInfo.unit}
                                        color={cardInfo.color}
                                        icon={cardInfo.icon}
                                        descriptionValue={cardInfo.descriptionValue}
                                        additionalDes={cardInfo?.additionalDes}
                                    />
                                </div>
                            ))}
                    </div>
                </div>
            </div >
        )
    }

    return (
        <div>
            <div className="w-100">
                {BreadcrumbHelper.buildBreadcrumb([
                    {
                        title: 'Báo cáo đối soát chủ mặt bằng',
                        onPress: () => {
                            router.navigate(-1);
                        },
                    },
                    { title: 'Chi tiết báo cáo đối soát' },
                ])}
            </div>

            <div className="m-4 mt-16">
                {/* thong tin Dai ly */}
                <CustomCard
                    showHeader={false}
                    additionalClassName="rounded-lg overflow-hidden"
                    bgTransparent={true}
                    showBorder={false}
                    body={
                        <div className="d-flex flex-column">
                            <div
                                className={`col-12 p-0 h-100`}
                            >
                                <SaleReconciliationInfo />

                                <GroupCommissionReconciliationInfo />

                            </div>

                        </div>
                    }
                />

                {/* table */}
                <div className="pt-0">
                    <AppTabs
                        tabs={tabs}
                        activeTab={selectedTab}
                        handleClick={handleSelectedTab}
                        className="mt-2 mr-3"
                        additionalClassName=""
                    />
                    <div
                        className="bg-white shadow"
                        style={{
                            borderLeft: `1px solid ${AppColors.gray[300]}`,
                            borderRight: `1px solid ${AppColors.gray[300]}`,
                            borderBottom: `1px solid ${AppColors.gray[300]}`,
                            borderRadius: '0px 10px 10px 10px',
                        }}
                    >
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={selectedTab}
                                initial={{ y: 10, opacity: 0 }}
                                animate={{ y: 0, opacity: 1 }}
                                exit={{ y: -10, opacity: 0 }}
                                transition={{ duration: 0.2 }}
                            >
                                {selectedTab == 'Doanh thu theo mặt bằng' && (
                                    <div className="px-5 pb-3 pt-4">{landlordRevenueView}</div>
                                )}
                                {selectedTab == 'Tiền điện trạm sạc' && (
                                    <div className="px-5 pb-3 pt-4">{stationEnergyView}</div>
                                )}
                                {selectedTab == 'Hóa đơn tiền điện' && (
                                    <div className="px-5 pb-3 pt-4">{energyBillView}</div>
                                )}
                            </motion.div>
                        </AnimatePresence>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default LandlordReconciliationDetailView;
